/**
 * pendingOverlayGate — pure render-gate helpers for the pane's pending
 * (thinking/progress) overlay widget.
 *
 * INCIDENT (owner reports, 2026-07-13 + 2026-07-14 fresh-incognito DR send):
 * the pane's pending widget rendered ONLY while
 * `processingByThread[activeThreadId]` was true. Any mid-turn release of
 * that flag — the Firestore-watcher release (PR #540), the empty-content
 * finalize path, tool onError releases, the not-handled fall-through —
 * blanked the pane AND (post-#540, chips driven by the same flag) every
 * chip/sidebar spinner simultaneously, while the extracted widget stayed
 * silently wedged in `state.messages`. Total blank: no indicator anywhere,
 * no way to tell a live turn from a dead one.
 *
 * FIX: the overlay is CONTENT, not rail state — it renders whenever a
 * pending-shaped message exists in the active thread's transcript,
 * independent of the processing flag. `state.messages` is structurally the
 * ACTIVE thread's transcript (background-thread turns route their updates to
 * per-thread storage, never the live pane — useGlobalChat routedUpdateById),
 * so the original "background turn must not animate the active pane" concern
 * cannot occur through this gate. The one deliberate suppression kept is
 * awaiting-input (MT-QA-BUG-009): while a tool waits on the user, the
 * animated widget must not imply computation.
 *
 * A side effect is intentional: a genuinely orphaned dead widget is now
 * VISIBLE (with its working stop button) instead of invisibly wedged — the
 * hydrate reconcilers already convert true orphans into Interrupted cards.
 */

import type { ChatMessage } from './types';

/** Gate for rendering the pane's pending-overlay row. */
export function shouldRenderPendingOverlay(
  hasPendingOverlay: boolean,
  inputStatus: string | undefined,
): boolean {
  if (!hasPendingOverlay) return false;
  // MT-QA-BUG-009: awaiting-input suppression preserved.
  return inputStatus !== 'awaiting_user_input';
}

/**
 * Source tags that must NOT hide the pending overlay when they appear with a
 * newer timestamp (the hasNewer suppression exists so the overlay vanishes
 * the moment real content lands — these are not "real content").
 */
export const OVERLAY_NON_HIDING_SOURCE_TAGS: ReadonlySet<string> = new Set([
  // Other pending states (don't hide each other)
  'attachment_read_pending',
  'stage1_assessment_pending',
  'immediate_thinking_pending',
  'research_result_streaming',
  'format_streaming',
  'translation_streaming',
  // Completed placeholders (empty, just cleanup markers)
  'attachment_read_complete',
  'stage1_assessment_complete',
  // Status/informational messages (not content)
  'deep_research_status',
  'deep_research_qualification',
  'attachment_analysis_success',
  'research_thinking',
  'research_thinking_empty',
  // Error states (shouldn't hide pending work)
  'attachment_read_error',
  'stage1_assessment_error',
  // 2026-07-14 hardening: the persisted greeting is seeded with a fresh
  // timestamp on new/empty threads — it must never mask a live turn's
  // widget on the first send.
  'welcome',
  // Queued-send acknowledgment (see useGlobalChat ENQUEUE branch) — it
  // narrates the queue, it is not the reply.
  'queued_notice',
  // Offline-held send acknowledgment (PR-2, useGlobalChat OFFLINE branch) —
  // narrates the hold, it is not the reply.
  'offline_queued_notice',
  // Interim progress note posted mid-task (owner report 2026-08-17: the
  // "digest will follow in this thread" bubble) — it narrates continuing
  // work, it is not the reply, and it must never retire the working widget.
  'prest0n_progress_note',
  // The note turn's reply to a mid-turn note (2026-09-15, PLAN-mid-turn-fold-in
  // §4.2) — Prest0n's own words about the running work, not the reply to it.
  'note_reply',
]);

/**
 * True when `m` is the kind of message whose newer timestamp should hide the
 * pending overlay (i.e., real content arrived). Invisible messages
 * (context-only) and the non-hiding tag families never hide it — previously
 * an invisible newer message could null the overlay while the widget stayed
 * extracted from the flow: a blank pane with work still running.
 */
export function messageCanHidePendingOverlay(m: ChatMessage): boolean {
  if (!m) return false;
  if (m.isContextOnly) return false;
  return !OVERLAY_NON_HIDING_SOURCE_TAGS.has(m.sourceTag || '');
}
